import { Redirect } from "react-router-dom"
import styled from "styled-components"

import { breakpoint, colors } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"

import { ImageBackdrop } from "src/components/ImageBackdrop/ImageBackdrop"
import { LoginForm } from "src/components/Login/LoginForm"
import { resetPasswordUrl } from "src/constants/hrefs"
import { useAppData } from "src/context/useAppData"
import { redirectToAuthService } from "src/data/auth/auth"
import { storedLocation } from "src/data/auth/authStorage"
import { useFetchInvitationInfo } from "src/data/organizations/queries/inviteQueries"
import { useAppLocation } from "src/hooks/useAppLocation"
import { useFlags } from "src/hooks/useFlags"
import { useTranslate } from "src/i18n/useTranslate"
import { DocumentHead } from "src/router/DocumentHead"
import { getInviteCodeFromUrl } from "src/router/organizationRoutes"
import { Routes } from "src/router/routes"
import { Divider } from "src/ui/Divider/Divider"
import Logo from "src/ui/icons/logo.svg"
import backdropImage from "src/ui/images/photo-building.jpg"
import { ExternalLink } from "src/ui/Link/ExternalLink"
import { InternalLink } from "src/ui/Link/InternalLink"
import {
  locationWithOrgId,
  mergeQueryParams,
} from "src/ui/Link/internalLinkUtil"
import { LoadingScreen } from "src/ui/LoadingScreen/LoadingScreen"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"
import { debug } from "src/utils/logger"

export function Login() {
  const { oauthEnabled } = useFlags()
  const { authState, activeOrg, user, loading } = useAppData()
  const authenticated = !!authState.authorization
  const userLoggedIn = !!user && !loading

  const location = useAppLocation()

  const { t, langKeys } = useTranslate()

  const inviteCode = getInviteCodeFromUrl()
  const title = inviteCode
    ? t(langKeys.organizations_team_join)
    : t(langKeys.sign_in)

  const fetchInvitationInfo = useFetchInvitationInfo({
    inviteCode: inviteCode || "",
    options: {
      enabled: !!inviteCode,
    },
  })

  const invitationinfo = fetchInvitationInfo.data

  if (fetchInvitationInfo.isInitialLoading) {
    return (
      <LoadingScreen
        debugInfo={<div>Fetching invitation information ...</div>}
      />
    )
  }

  if (oauthEnabled && !authenticated) {
    // If enabled by accident, open http://localhost:8001?oauthEnabled=off
    redirectToAuthService(location)
    return (
      <LoadingScreen
        debugInfo={<div>Redirecting to OAuth login page...</div>}
      />
    )
  }

  if (userLoggedIn) {
    const storedLocationData = storedLocation.pop()
    const baseLocation = {
      ...(storedLocationData ?? Routes.Dashboard.location()),
      // We need to keep search if present:
      search: mergeQueryParams(location.search, storedLocationData?.search),
    }
    const redirectLocation = locationWithOrgId({
      orgId: activeOrg?.id,
      location: baseLocation,
    })

    debug.log(`${location.pathname} redirecting to`, redirectLocation)
    return <Redirect to={redirectLocation} />
  }

  return (
    <div>
      <DocumentHead
        title={t(langKeys.sign_in_meta_title)}
        metaTags={[
          {
            name: "description",
            content: t(langKeys.sign_in_meta_description),
          },
        ]}
      />

      <ImageBackdrop
        landscapeImageUrl={backdropImage}
        portraitImageUrl={backdropImage}
        backgroundColor={colors.neutral}
      >
        <LoginCard>
          <LogoBox>
            <Logo width={90} />
          </LogoBox>

          <Text variant="heading1" marginBottom={spacing.L}>
            {title}
          </Text>

          <LoginForm prefillEmail={invitationinfo?.invitation_email} />

          <Divider $margin={spacing.L} />

          <AccountSetup>
            <Text variant="bodyS" color="secondary">
              {!invitationinfo?.invitation_email && (
                <p>
                  {t(langKeys.sign_in_no_account)}{" "}
                  <InternalLink to={{ ...location, pathname: "/signup" }}>
                    {t(langKeys.create_an_account)}
                  </InternalLink>
                </p>
              )}

              <p>
                <ExternalLink
                  href={resetPasswordUrl()}
                  showExternalIcon={false}
                >
                  {t(langKeys.forgot_password)}
                </ExternalLink>
              </p>
            </Text>
          </AccountSetup>
        </LoginCard>
      </ImageBackdrop>
    </div>
  )
}

const LoginCard = styled(MCard)`
  padding: ${spacing.L};
  max-width: 65ch;

  @media (${breakpoint.mediumUp}) {
    padding: ${spacing.XL2};
  }
`

const LogoBox = styled.div`
  margin-bottom: ${spacing.XL};
`

const AccountSetup = styled.div`
  margin-top: ${spacing.L};
`
